import { ChangeDetectionStrategy, Component, computed, effect, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { AdvancedCommerceListingFiltersAttributeComponentDef } from '@df/module-advanced-commerce';
import { RangeSliderChangeContext } from '@df/ui/form/range-slider/change-context';
import { RangeSliderOptions } from '@df/ui/form/range-slider/options';
import { RangeSliderModule } from '@df/ui/form/range-slider/range-slider.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import debounce from 'lodash-es/debounce';

@Component({
    selector: 'ac-filter-attribute-range',
    templateUrl: '../../../../catalog/ui/listing/filter/filter-attribute-range.html',
    styleUrls: ['../../../../catalog/ui/listing/filter/filter-attribute-range.scss'],
    standalone: true,
    imports: [ZippyModule, TranslationModule, FormsModule, PriceComponent, RangeSliderModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceFilterAttributeRangeComponent
    extends AdvancedCommerceListingFiltersAttributeComponentDef
    implements OnInit, OnChanges
{
    protected _rangeMin?: number;
    protected _rangeMax?: number;
    protected _min?: number;
    protected _max?: number;

    options: RangeSliderOptions = new RangeSliderOptions();

    handleModelChangeFrom = debounce(this.handleModelChangeFromDebounced, 300);
    handleModelChangeTo = debounce(this.handleModelChangeToDebounced, 300);
    minMax = computed(() => {
        return this.store.filtersPriceTo() - 1 || this.filter().meta?.max || 0;
    });
    maxMin = computed(() => {
        return this.store.filtersPriceFrom() + 1 || this.filter().meta?.min || 0;
    });

    constructor() {
        super();

        effect(() => {
            const priceFrom = this.store.filtersPriceFrom();
            const priceTo = this.store.filtersPriceTo();

            if (priceFrom === 0 && priceTo === 0) {
                this.rangeMin = this.metaMin;
                this.rangeMax = this.metaMax;
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    protected handleModelChangeFromDebounced(value?: number): void {
        const min = this.filter().meta?.min || 0;
        if (!value) {
            value = min;
        }
        if (value < min) {
            value = min;
        } else if (value > this.minMax()) {
            value = this.minMax();
        }
        this.store.filtersPriceFromSet(this.filter(), value);
        this.changeDetectorRef.detectChanges();
    }

    protected handleModelChangeToDebounced(value?: number): void {
        const max = this.filter().meta?.max || 0;
        if (!value) {
            value = max;
        }
        if (value > max) {
            value = max;
        } else if (value < this.maxMin()) {
            value = this.maxMin();
        }
        this.store.filtersPriceToSet(this.filter(), value);
        this.changeDetectorRef.detectChanges();
    }

    get min(): number | undefined {
        return this.filter().meta?.min;
    }

    get max(): number | undefined {
        return this.filter().meta?.max;
    }

    get minModel(): number | undefined {
        return this.store.filtersPriceFrom() || this.filter().meta?.min;
    }

    set minModel(value: number | undefined) {
        if (!!this.filter().meta) {
            this.handleModelChangeFrom(value);
        }
    }

    get maxModel(): number | undefined {
        return this.store.filtersPriceTo() || this.filter().meta?.max;
    }

    set maxModel(value: number | undefined) {
        if (!!this.filter().meta) {
            this.handleModelChangeTo(value);
        }
    }

    get label(): string | undefined {
        return this.filter().label;
    }

    set rangeMin(value: number) {
        this._rangeMin = value;
        this._min = value;
    }

    get rangeMin() {
        return this._rangeMin || 0;
    }

    set rangeMax(value: number) {
        this._rangeMax = value;
        this._max = value;
    }

    get rangeMax() {
        return this._rangeMax || 0;
    }

    get metaMax(): number {
        return this.filter().meta?.max || 1000;
    }

    get metaMin(): number {
        return this.filter().meta?.min || 0;
    }

    ngOnInit() {
        this.rangeMin = this.metaMin;
        this.rangeMax = this.metaMax;

        if (this.store.filtersPriceFrom()) {
            this.rangeMin = Math.max(this.metaMin, this.store.filtersPriceFrom());
        }
        if (this.store.filtersPriceTo()) {
            this.rangeMax = Math.min(this.metaMax, this.store.filtersPriceTo());
        }

        this.updateOptions();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes['options']) {
            this.updateOptions();
        }
    }

    onUserChange(event: RangeSliderChangeContext) {
        this.rangeMin = this._min = event.value;
        this.rangeMax = this._max = event.highValue || 0;
        this.changeDetectorRef.detectChanges();
    }

    onUserChangeEnd(event: RangeSliderChangeContext) {
        if (this.filter().meta) {
            this.store.filtersPriceFromSet(this.filter(), event.value);
            this.store.filtersPriceToSet(this.filter(), event.highValue || 0);
        }
    }

    protected updateOptions() {
        this.options = {
            floor: this.metaMin,
            ceil: this.metaMax,
            step: 0.01,
            hideLimitLabels: true,
            hidePointerLabels: true,
            outputEventsInterval: 50
        };
    }
}
